import React, { useEffect, useMemo, useRef, useState } from "react";
import { Container } from "../styles";
import { CardHistoricoProps } from "./types";
import { IItemHistoricoPlano, IItemHistoricoServico } from "../../../types";
import { ChevronDown, DotIcon, Laptop, Smartphone, Users, Cpu, LucideIcon } from "lucide-react";

const ehAcaoSistema = (item: IItemHistoricoPlano | IItemHistoricoServico): boolean => {
  const tipo = (item.tipo_responsavel || '').toLowerCase().trim();
  if (tipo === 'sistema') return true;
  const itemPlano = item as IItemHistoricoPlano;
  if (itemPlano.origem_evento === 'TEMPLATE_GLOBAL') return true;
  return (item.dispositivo_tipo || '').toLowerCase() === 'sistema';
};

const getDeviceIcon = (item: IItemHistoricoPlano | IItemHistoricoServico): LucideIcon => {
  if (ehAcaoSistema(item)) return Cpu;
  const aparelho = (item.dispositivo_tipo || 'desktop').toLowerCase();
  return aparelho === 'mobile' ? Smartphone : Laptop;
};

interface CardHistoricoBaseProps extends CardHistoricoProps {
  icon: { Icon: LucideIcon; color: string };
  temExpansaoDisponivel: boolean;
  expandedContent?: React.ReactNode;
  tituloCustomizado?: string;
}

export default function CardHistoricoBase({
  item,
  temExpansaoDisponivel,
  isExpanded,
  onToggleExpand,
  expandedContent,
  tituloCustomizado
}: CardHistoricoBaseProps) {
  const DeviceIcon = getDeviceIcon(item);
  const containerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number>(0);
  const [deveRenderizarConteudo, setDeveRenderizarConteudo] = useState(false);
  const expandedContentWithState = useMemo(() => {
    if (!expandedContent || !React.isValidElement(expandedContent)) {
      return expandedContent;
    }
    const conteudoComExpansao = expandedContent as React.ReactElement<{ isExpanded?: boolean }>;
    return React.cloneElement(conteudoComExpansao, { isExpanded });
  }, [expandedContent, isExpanded]);

  useEffect(() => {
    if (innerRef.current && expandedContent && isExpanded) {
      const measureHeight = () => {
        if (innerRef.current) {
          setContentHeight(innerRef.current.scrollHeight);
        }
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(measureHeight);
      });
    } else if (!isExpanded) {
      setContentHeight(0);
    }
  }, [expandedContent, isExpanded]);

  useEffect(() => {
    if (isExpanded && !deveRenderizarConteudo) {
      setDeveRenderizarConteudo(true);
    }
  }, [isExpanded, deveRenderizarConteudo]);

  // Formatar ID para exibição #E{id}
  const formatId = (id: number) => {
    return `#E${id}`;
  };

  return (
    <Container 
      ref={containerRef}
      className={temExpansaoDisponivel ? 'clicavel' : ''}
      onClick={temExpansaoDisponivel ? onToggleExpand : undefined}
    >
      <div className="top">
        <div className="acao">
          {tituloCustomizado || item.acao}
        </div>
        <div className="infos">
          <DeviceIcon className="icone-top" />
          <DotIcon size={10} className="icone-top"/>
          <div className="codigo">
            {formatId(item.id)}
          </div>
          <div className="chevron-container" onClick={(e) => {
            e.stopPropagation();
            if (temExpansaoDisponivel) {
              onToggleExpand();
            }
          }}>
            <ChevronDown 
              className={`icone-top chevron ${isExpanded ? 'expanded' : ''}`}
            />
          </div>
        </div>
      </div>
      <div className="bottom">
        {item.tipo_responsavel !== 'Sistema' && <Users/>}
        {item.nome_responsavel || ''}
        <div className="tipo_responsavel">
          {item.tipo_responsavel}
        </div>
      </div>
      
      {expandedContentWithState && deveRenderizarConteudo && (
        <div 
          className={`expanded-content-wrapper ${isExpanded ? 'expanded' : 'collapsed'}`}
          style={{ height: isExpanded ? (contentHeight > 0 ? `${contentHeight}px` : 'auto') : '0px' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div ref={innerRef} className="expanded-content-inner">
            {expandedContentWithState}
          </div>
        </div>
      )}
    </Container>
  );
}
